<script setup>
import { ref } from 'vue';
import VideoCover from '@/components/video-cover/VideoCover.vue';
import Podcast from '@/components/podcast/Podcast.vue';
import { GA } from '@/mixins/Ga';

const props = defineProps({
    data: { type: Object, default: () => {} },
    coverColor: {
        type: String,
        default: '',
    },
});

const type = ref('single');
switch (props.data.type) {
    case 'imageImage':
    case 'imageVideo':
    case 'podcastImage':
        type.value = 'dual';
        break;
    case 'imageCredits':
        type.value = 'singleCredits';
        break;
    case 'ad':
        type.value = 'responsive';
        break;
    default:
        type.value = 'single';
}

const ga = new GA({ category: 'ads', action: 'clickAd' });
</script>

<template>
    <div
        class="Medias flex flex-col justify-end px-6 xl:flex-row"
        :class="{ 'full-width px-6 xl:px-12': type === 'dual' }"
    >
        <div
            v-if="type === 'single'"
            class="Medias__single !aspect-h-9 !aspect-w-16 w-full"
        >
            <Cover
                v-if="data.type === 'image'"
                :color="coverColor"
                :data="data.data.image"
            />
            <VideoCover
                v-if="data.type === 'video'"
                :cover-color="coverColor"
                :data="data.data.video[0]"
            />
        </div>

        <div
            v-if="type === 'singleCredits'"
            class="Medias__single !aspect-h-9 !aspect-w-16 w-full"
            :class="{ 'has-credits': data.data.image && data.data.image[0] && data.data.image[0].credits }"
        >
            <Cover
                v-if="data.type === 'imageCredits'"
                :color="coverColor"
                :data="data.data.image[0].image"
                :credits="data.data.image[0].credits"
            />
        </div>

        <template v-if="type === 'dual'">
            <div
                class="Medias__left mr-12 h-60 w-full shrink-0 grow-0 basis-auto md:h-112 xl:mr-8 xl:h-148 xl:basis-[28rem]"
                :class="{
                    'has-credits mb-12': data.data.images && data.data.images.length > 0 && data.data.images[0].leftcredits,
                }"
            >
                <Cover
                    v-if="data.type === 'imageImage'"
                    :color="coverColor"
                    :data="data.data.images[0].left"
                    :credits="data.data.images[0].leftcredits"
                    class="relative h-full w-full"
                    creditsclass="pl-2 xl:pl-5"
                />
                <Cover
                    v-if="data.type === 'imageVideo'"
                    :color="coverColor"
                    :data="data.data.image"
                    class="relative h-full w-full"
                />
                <Podcast
                    v-if="data.type === 'podcastImage'"
                    :cover-color="coverColor"
                    :data="data.data.podcast[0]"
                />
            </div>
            <div
                class="Medias__right ml-6 mt-6 h-60 md:ml-12 md:mt-12 md:h-112 xl:ml-0 xl:mt-16 xl:h-148 xl:w-full"
                :class="{
                    'has-left-credits mt-12': data.data.images && data.data.images.length > 0 && data.data.images[0].leftcredits,
                    'has-credits mb-12': data.data.images && data.data.images.length > 0 && data.data.images[0].rightcredits,
                }"
            >
                <Cover
                    v-if="data.type == 'imageImage'"
                    :color="coverColor"
                    :data="data.data.images[0].right"
                    :credits="data.data.images[0].rightcredits"
                    class="relative h-full w-full"
                />
                <Cover
                    v-if="data.type == 'podcastImage'"
                    :color="coverColor"
                    :data="data.data.image"
                    class="relative h-full w-full"
                />
                <VideoCover
                    v-if="data.type == 'imageVideo'"
                    :cover-color="coverColor"
                    :data="data.data.video[0]"
                />
            </div>
        </template>
        <template v-if="data.data.entry && data.data.entry[0].adImage && data.data.entry[0].adImageMobile && type == 'responsive'">
            <a
                :href="data.data.entry[0].adUrl"
                target="_blank"
                class="Medias__ad block w-full px-6 md:px-12"
                v-on:click="ga.send({ label: 'ad' + data.data.entry[0].title })"
            >
                <Cover
                    v-if="data.data.entry[0].adImage && (landscape || tablet)"
                    :color="coverColor"
                    :data="data.data.entry[0].adImage"
                />
                <Cover
                    v-if="data.data.entry[0].adImageMobile && portrait"
                    :color="coverColor"
                    :data="data.data.entry[0].adImageMobile"
                />
            </a>
        </template>
    </div>
</template>
